import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaMapMarkerAlt, FaClock, FaBriefcase, FaCalendar,
  FaArrowRight, FaStar, FaHeart,
  FaBriefcase as FaBag, FaGlobe,
  FaMoneyBillWave, FaGraduationCap, FaHeartbeat, FaChartLine, FaHandshake,
  FaEnvelope,
} from 'react-icons/fa';
import PageTransition from '../components/PageTransition';
import { getCareers } from '../utils/api';
import {
  fadeInUp, scaleIn,
  staggerContainer, staggerContainerSlow, viewport,
} from '../utils/animations';

const departments = ['all', 'Engineering', 'Design', 'Marketing', 'Sales'];
const types = ['all', 'full-time', 'part-time', 'contract', 'internship'];

const DEMO_JOBS = [
  {
    _id: '1',
    slug: 'full-stack-developer',
    title: 'Full Stack Developer',
    department: 'Engineering',
    location: 'Remote',
    type: 'full-time',
    experience: '3-5 Years',
    description: 'We are looking for a talented Full Stack Developer to join our Engineering team and help build cutting-edge web applications for our clients. You will work across the entire stack, from crafting beautiful, responsive UIs to designing robust APIs and scalable backend systems.',
    active: true,
    postedDate: new Date().toISOString(),
  },
  {
    _id: '2',
    slug: 'sales-representative',
    title: 'Sales Representative',
    department: 'Sales',
    location: 'Remote',
    type: 'full-time',
    experience: '2-4 Years',
    description: 'We are seeking a driven Sales Representative to join our growing Sales team. You will identify and close new business opportunities, manage client relationships, and help shape our go-to-market strategy.',
    active: true,
    postedDate: new Date().toISOString(),
  },
  {
    _id: '3',
    slug: 'ui-ux-designer',
    title: 'UI/UX Designer',
    department: 'Design',
    location: 'Remote',
    type: 'full-time',
    experience: '2-4 Years',
    description: 'We are looking for a creative and user-obsessed UI/UX Designer to join our Design team. You will own the end-to-end design process, from user research and wireframing to high-fidelity prototypes and final handoff. Working closely with engineers and product managers, you will turn complex problems into intuitive, beautiful digital experiences.',
    active: true,
    postedDate: new Date().toISOString(),
  },
];

const typeStyles = {
  'full-time':  'bg-emerald-900/30 border-emerald-700/40 text-emerald-400',
  'part-time':  'bg-amber-900/30 border-amber-700/40 text-amber-400',
  'contract':   'bg-blue-900/30 border-blue-700/40 text-blue-400',
  'internship': 'bg-purple-900/30 border-purple-700/40 text-purple-400',
};

const benefits = [
  { icon: FaMoneyBillWave, title: 'Competitive Salary',   desc: 'Industry-leading compensation and equity packages that reward your talent.' },
  { icon: FaGlobe,         title: 'Remote-First',          desc: 'Fully flexible work arrangements, work from anywhere in the world.' },
  { icon: FaGraduationCap, title: 'Learning & Growth',     desc: 'Dedicated budget for courses, conferences, and professional development.' },
  { icon: FaHeartbeat,     title: 'Health & Wellness',     desc: 'Comprehensive health cover for you and your family.' },
  { icon: FaChartLine,     title: 'Fast Career Growth',    desc: 'Flat hierarchy, big responsibilities, and a clear path to leadership.' },
  { icon: FaHandshake,     title: 'Team Culture',          desc: 'Regular off-sites, team events, and a culture built on trust and transparency.' },
];

const Careers = () => {
  const [jobs, setJobs] = useState(DEMO_JOBS);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const loading = false;

  useEffect(() => { fetchJobs(); }, []); // eslint-disable-line

  const fetchJobs = async () => {
    try {
      const data = await getCareers({ active: true });
      const list = Array.isArray(data) ? data : (data?.data || []);
      if (list.length) setJobs(list);
    } catch (error) {
      // keep DEMO_JOBS already in state
    }
  };

  const filteredJobs = jobs.filter(job => {
    const deptMatch = selectedDepartment === 'all' || job.department === selectedDepartment;
    const typeMatch = selectedType === 'all' || job.type === selectedType;
    return deptMatch && typeMatch;
  });

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const getTypeLabel = (type) =>
    type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  return (
    <PageTransition>
      <div className="bg-transparent min-h-screen">

        {/* ── Hero ─────────────────────────────────────────────── */}
        <section className="relative pt-36 pb-24 overflow-hidden">
          <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
          <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-gray-700/10 blur-3xl pointer-events-none" />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="text-center max-w-4xl mx-auto"
            >
              {/* Badge */}
              <motion.div variants={fadeInUp} className="inline-flex mb-6">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 backdrop-blur-sm border border-gray-700 rounded-full badge-glow">
                  <FaStar className="text-gray-400 text-xs" />
                  <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">We're Hiring</span>
                </div>
              </motion.div>

              {/* Headline */}
              <motion.h1
                variants={fadeInUp}
                className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight"
              >
                <span className="text-white">Shape the</span>
                <br />
                <span className="animated-gradient-text">Future With Us</span>
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                variants={fadeInUp}
                className="text-gray-400 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-12"
              >
                Join a team of passionate engineers, designers, and strategists building
                products that push the boundaries of what's possible.
              </motion.p>

              {/* Stats row */}
              <motion.div
                variants={fadeInUp}
                className="flex flex-wrap items-center justify-center gap-8"
              >
                {[
                  { value: `${jobs.length}+`, label: 'Open Positions' },
                  // { value: '5',              label: 'Departments' },
                  { value: 'Remote',         label: 'Work Options' },
                ].map((s, i) => (
                  <div key={i} className="text-center">
                    <div className="text-4xl font-black text-white">{s.value}</div>
                    <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase mt-1">{s.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ── Sticky Filters ────────────────────────────────────── */}
        <section className="sticky top-[70px] z-30 bg-gray-950/80 backdrop-blur-md border-b border-gray-800/60 py-5">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Department */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs font-semibold tracking-widest text-gray-500 uppercase shrink-0">Dept:</span>
                {departments.map((dept) => (
                  <button
                    key={dept}
                    onClick={() => setSelectedDepartment(dept)}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 border ${
                      selectedDepartment === dept
                        ? 'bg-white text-gray-950 border-white'
                        : 'bg-transparent text-gray-400 border-gray-700 hover:border-gray-500 hover:text-white'
                    }`}
                  >
                    {dept === 'all' ? 'All' : dept}
                  </button>
                ))}
              </div>

              <div className="hidden sm:block w-px h-5 bg-gray-700" />

              {/* Type */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs font-semibold tracking-widest text-gray-500 uppercase shrink-0">Type:</span>
                {types.map((type) => (
                  <button
                    key={type}
                    onClick={() => setSelectedType(type)}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 border ${
                      selectedType === type
                        ? 'bg-white text-gray-950 border-white'
                        : 'bg-transparent text-gray-400 border-gray-700 hover:border-gray-500 hover:text-white'
                    }`}
                  >
                    {type === 'all' ? 'All Types' : getTypeLabel(type)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Jobs Listing ──────────────────────────────────────── */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 dot-pattern opacity-20 pointer-events-none" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-4xl">

            {/* Loading */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-32 gap-4">
                <div className="w-10 h-10 rounded-full border-2 border-gray-700 border-t-white animate-spin" />
                <p className="text-gray-500 text-sm tracking-wide">Loading open roles…</p>
              </div>
            )}

            {/* Empty state */}
            {!loading && filteredJobs.length === 0 && (
              <motion.div
                variants={fadeInUp}
                initial="hidden"
                animate="visible"
                className="flex flex-col items-center justify-center py-32 gap-6"
              >
                <FaBag className="text-gray-700 text-5xl" />
                <p className="text-gray-400 text-lg">No openings match your current filters.</p>
                <button
                  onClick={() => { setSelectedDepartment('all'); setSelectedType('all'); }}
                  className="px-6 py-2.5 rounded-xl border border-gray-700 text-gray-300 text-sm font-semibold hover:border-gray-500 hover:text-white transition-all duration-300"
                >
                  Clear Filters
                </button>
              </motion.div>
            )}

            {/* Cards */}
            {!loading && filteredJobs.length > 0 && (
              <motion.div
                variants={staggerContainerSlow}
                initial="hidden"
                animate="visible"
                className="flex flex-col gap-5"
              >
                {filteredJobs.map((job) => (
                  <motion.div
                    key={job._id}
                    variants={fadeInUp}
                    whileHover={{ y: -4, transition: { duration: 0.2 } }}
                    className="glass-card rounded-2xl p-7 group card-hover-lift shine-hover"
                  >
                    {/* Top row */}
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="text-white font-black text-xl leading-tight">{job.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold border tracking-wide ${typeStyles[job.type] || 'bg-gray-800 border-gray-700 text-gray-300'}`}>
                          {getTypeLabel(job.type)}
                        </span>
                      </div>
                      <span className="px-3 py-1.5 rounded-lg bg-gray-800/80 border border-gray-700 text-gray-300 text-xs font-semibold uppercase tracking-widest">
                        {job.department}
                      </span>
                    </div>

                    {/* Meta row */}
                    <div className="flex flex-wrap gap-5 mb-5 pb-5 border-b border-gray-800">
                      {job.location && (
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                          <FaMapMarkerAlt className="text-gray-500 text-xs" />
                          <span>{job.location}</span>
                        </div>
                      )}
                      {job.experience && (
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                          <FaBriefcase className="text-gray-500 text-xs" />
                          <span>{job.experience}</span>
                        </div>
                      )}
                      {job.postedDate && (
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                          <FaCalendar className="text-gray-500 text-xs" />
                          <span>Posted {formatDate(job.postedDate)}</span>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-gray-400 text-sm leading-relaxed mb-5 line-clamp-3">{job.description}</p>

                    {/* Salary */}
                    {job.salary && (
                      <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-800/50 border-l-2 border-gray-500 mb-5">
                        <FaClock className="text-gray-400 text-sm shrink-0" />
                        <span className="text-white font-semibold text-sm">{job.salary}</span>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-3 pt-5 border-t border-gray-800">
                      <Link
                        to={`/careers/${job.slug}`}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-gray-700 text-gray-300 text-sm font-semibold hover:border-gray-500 hover:text-white transition-all duration-300 hover:-translate-y-0.5"
                      >
                        View Details
                        <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform duration-300" />
                      </Link>
                      {job.applyUrl && (
                        <a
                          href={job.applyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-white text-gray-950 text-sm font-bold hover:bg-gray-100 transition-all duration-300 hover:-translate-y-0.5 shine-hover"
                        >
                          Apply Now
                          <FaArrowRight className="text-xs" />
                        </a>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>
        </section>

        {/* ── Benefits ─────────────────────────────────────────── */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 dot-pattern pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-gray-950 to-transparent pointer-events-none" />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="text-center mb-16"
            >
              <motion.div variants={fadeInUp} className="inline-flex mb-6">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 border border-gray-700 rounded-full">
                  <FaHeart className="text-gray-400 text-xs" />
                  <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">Perks & Benefits</span>
                </div>
              </motion.div>
              <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-white mb-4">
                Why Work <span className="animated-gradient-text">With Us?</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-gray-400 max-w-xl mx-auto">
                We invest in our people, because great products are built by people who feel supported and inspired.
              </motion.p>
            </motion.div>

            <motion.div
              variants={staggerContainerSlow}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {benefits.map((b, i) => (
                <motion.div
                  key={i}
                  variants={scaleIn}
                  whileHover={{ y: -6, transition: { duration: 0.25 } }}
                  className="glass-card rounded-2xl p-8 group card-hover-lift shine-hover"
                >
                  <div className="w-12 h-12 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center mb-5">
                    <b.icon className="text-gray-300 text-xl" />
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">{b.title}</h3>
                  <p className="text-gray-400 leading-relaxed text-sm">{b.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── CTA ──────────────────────────────────────────────── */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/0 via-gray-800/30 to-gray-900/0 pointer-events-none" />
          <div className="spotlight w-96 h-96 top-1/2 left-1/2" />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              <motion.div variants={fadeInUp} className="inline-flex mb-6">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 backdrop-blur-sm border border-gray-700 rounded-full badge-glow">
                  <FaEnvelope className="text-gray-400 text-xs" />
                  <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">Get In Touch</span>
                </div>
              </motion.div>

              <motion.h2
                variants={fadeInUp}
                className="text-4xl md:text-5xl font-black text-white mb-6"
              >
                Don't see a role that fits?<br />
                <span className="animated-gradient-text">Reach out anyway.</span>
              </motion.h2>

              <motion.p
                variants={fadeInUp}
                className="text-gray-400 text-lg mb-10 max-w-xl mx-auto"
              >
                We're always interested in meeting exceptional people. Send us your details and we'll keep you in mind.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  className="group flex items-center gap-2 px-8 py-4 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:-translate-y-1 shine-hover"
                >
                  Send Your Details
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                <Link
                  to="/about"
                  className="group flex items-center gap-2 px-8 py-4 border border-gray-700 text-gray-300 font-semibold rounded-xl hover:border-gray-500 hover:text-white transition-all duration-300 hover:-translate-y-1"
                >
                  Learn About Us
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

      </div>
    </PageTransition>
  );
};

export default Careers;
